import React, { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { DeviceInfo } from '@grosync/common';
import { Sym, IconButton } from '../../ui';
import { createDevice } from '../../api/devices';
import { fetchFarms } from '../../api/farms';
import { useDeviceModelOptions } from '../device-models/options';

const inputStyle: React.CSSProperties = {
  width: '100%',
  height: 44,
  padding: '0 14px',
  borderRadius: 12,
  border: '1px solid var(--md-sys-color-outline-variant)',
  background: 'var(--md-sys-color-surface)',
  color: 'var(--md-sys-color-on-surface)',
  font: '500 14px/1 var(--gs-font-kr)',
  boxSizing: 'border-box',
};

function Field({ label, children }: { label: string; children: React.ReactNode }): React.ReactElement {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <span style={{ font: '600 12px/1 var(--gs-font-kr)', color: 'var(--md-sys-color-on-surface-variant)' }}>
        {label}
      </span>
      {children}
    </label>
  );
}

export function AddDeviceModal({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: (d: DeviceInfo) => void;
}): React.ReactElement {
  const qc = useQueryClient();
  const options = useDeviceModelOptions();
  const farmsQuery = useQuery({ queryKey: ['farms'], queryFn: fetchFarms });
  const farms = farmsQuery.data ?? [];

  const [modelId, setModelId] = useState(options[0]?.id ?? '');
  const [name, setName] = useState('');
  const [farmId, setFarmId] = useState('');
  const [cropType, setCropType] = useState('');
  const [installedAt, setInstalledAt] = useState(new Date().toISOString().slice(0, 10));

  const model = useMemo(() => options.find((o) => o.id === modelId), [options, modelId]);

  const mutation = useMutation({
    mutationFn: (): Promise<DeviceInfo> => {
      if (!model) throw new Error('모델을 선택하세요');
      return createDevice({
        name: name.trim(),
        type: model.deviceType,
        installedAt,
        firmwareVersion: model.firmware,
        sensors: model.sensors,
        modelCode: model.code,
        deviceModelId: model.code,
        model: model.model,
        ...(cropType.trim() ? { cropType: cropType.trim() } : {}),
        ...(farmId ? { farmId } : {}),
      });
    },
    onSuccess: (device) => {
      qc.invalidateQueries({ queryKey: ['devices'] });
      onCreated(device);
    },
  });

  const canSave = !!model && name.trim().length > 0 && !mutation.isPending;

  return (
    <>
      <div onClick={onClose} style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.32)', zIndex: 30 }} />
      <div
        style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 460,
          maxHeight: '86%',
          zIndex: 31,
          background: 'var(--md-sys-color-surface)',
          border: '1px solid var(--md-sys-color-outline-variant)',
          borderRadius: 20,
          boxShadow: '0 24px 60px -16px rgba(0,0,0,0.45)',
          display: 'flex',
          flexDirection: 'column',
          animation: 'modalIn 220ms cubic-bezier(.2,0,0,1)',
        }}
      >
        <div style={{ padding: '18px 20px', display: 'flex', alignItems: 'center', gap: 8, borderBottom: '1px solid var(--md-sys-color-outline-variant)' }}>
          <span style={{ flex: 1, font: '700 16px/22px var(--gs-font-kr)', color: 'var(--md-sys-color-on-surface)' }}>
            장비 추가
          </span>
          <IconButton icon="close" size={22} onClick={onClose} />
        </div>

        <div style={{ padding: '20px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 16 }}>
          <Field label="장비모델">
            <select value={modelId} onChange={(e) => setModelId(e.target.value)} style={inputStyle}>
              {options.map((o) => (
                <option key={o.id} value={o.id}>
                  {o.name} ({o.code})
                </option>
              ))}
            </select>
          </Field>
          <Field label="장비명">
            <input value={name} onChange={(e) => setName(e.target.value)} placeholder="예: 온실 1구역" style={inputStyle} />
          </Field>
          <Field label="농장 (선택)">
            <select value={farmId} onChange={(e) => setFarmId(e.target.value)} style={inputStyle}>
              <option value="">미할당</option>
              {farms.map((f) => (
                <option key={f.id} value={f.id}>{f.name}</option>
              ))}
            </select>
          </Field>
          <Field label="작물 (선택)">
            <input value={cropType} onChange={(e) => setCropType(e.target.value)} placeholder="예: 토마토" style={inputStyle} />
          </Field>
          <Field label="설치일">
            <input type="date" value={installedAt} onChange={(e) => setInstalledAt(e.target.value)} style={inputStyle} />
          </Field>
          {mutation.isError && (
            <span style={{ font: '500 12px/1.4 var(--gs-font-kr)', color: 'var(--gs-error)' }}>
              {(mutation.error as Error).message}
            </span>
          )}
        </div>

        <div style={{ padding: '14px 20px', borderTop: '1px solid var(--md-sys-color-outline-variant)', display: 'flex', gap: 10 }}>
          <button
            type="button"
            onClick={onClose}
            style={{ flex: 1, height: 46, borderRadius: 12, cursor: 'pointer', background: 'transparent', border: '1px solid var(--md-sys-color-outline)', color: 'var(--md-sys-color-on-surface)', font: '600 14px/1 var(--gs-font-kr)' }}
          >
            취소
          </button>
          <button
            type="button"
            disabled={!canSave}
            onClick={() => mutation.mutate()}
            style={{ flex: 1.4, height: 46, borderRadius: 12, border: 0, cursor: canSave ? 'pointer' : 'not-allowed', opacity: canSave ? 1 : 0.5, background: 'var(--md-sys-color-primary)', color: 'var(--md-sys-color-on-primary)', font: '600 14px/1 var(--gs-font-kr)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}
          >
            <Sym name="add" size={18} />
            {mutation.isPending ? '등록 중…' : '장비 등록'}
          </button>
        </div>
      </div>
    </>
  );
}
